import type { ResolvedDocument, ResolvedNode } from "../resolve/resolved.js";
import type { GeometryMap, NodeGeometry, SectionGeometry } from "./geometry.js";
import type { PageGeometry } from "./page-geometry.js";

/* ------------------------------------------------------------------ */
/* Output model                                                        */
/* ------------------------------------------------------------------ */

export interface RepeatedHeader {
  tableKey: string;
  height: number;
  left: number;
  width: number;
}

/**
 * A clip window over a section's full render, placed on a page.
 * `[from, to)` are y coordinates in the section's own coordinate space.
 */
export interface PageFragment {
  sectionKey: string;
  from: number;
  to: number;
  /** Table headers to repeat above the clipped content (tables cut at `from`). */
  repeatHeaders: RepeatedHeader[];
  /** Y position of the fragment within the page content box. */
  y: number;
  /** Rendered height: (to - from) + repeated header heights. */
  height: number;
  /** True when this fragment continues a section started on an earlier page. */
  continued: boolean;
  /** True when the section continues on a later page. */
  continues: boolean;
}

export interface Page {
  index: number;
  number: number;
  fragments: PageFragment[];
  /** Whether page 1 variants of header/footer apply. */
  first: boolean;
}

export interface LayoutWarning {
  code: "forced-split" | "region-overflow" | "oversized-header" | "empty-document" | "no-op-break";
  message: string;
  nodeId?: string;
  sectionKey?: string;
  page?: number;
}

export interface PagedDocument {
  pages: Page[];
  totalPages: number;
  geometry: PageGeometry;
  warnings: LayoutWarning[];
}

export interface PaginateOptions {
  document: ResolvedDocument;
  geometry: GeometryMap;
  page: PageGeometry;
  /** Show at least this many pages (design canvas). */
  minPages?: number;
  /** Geometry for header/footer sections (for overflow warnings). */
  regionGeometry?: GeometryMap;
}

/* ------------------------------------------------------------------ */
/* Break finding                                                       */
/* ------------------------------------------------------------------ */

interface BreakCtx {
  geom: SectionGeometry;
  pageHeight: number;
}

function g(ctx: BreakCtx, node: ResolvedNode): NodeGeometry {
  return ctx.geom.nodes[node.key] ?? { top: 0, height: 0, left: 0, width: 0 };
}

const EPS = 0.01;

/**
 * Largest valid break position y with minY < y <= limit inside a list of
 * siblings, or null if none exists.
 */
export function bestBreak(ctx: BreakCtx, list: ResolvedNode[], limit: number, minY: number): number | null {
  // Keep-with-next means "stay with the next thing the reader can see". A
  // sibling that draws nothing — a conditional whose rule is false, an empty
  // repeater — must not satisfy it, or a heading is left stranded at the foot
  // of a page while its content starts the next one.
  const visible = (from: number, step: -1 | 1): ResolvedNode | undefined => {
    for (let j = from; j >= 0 && j < list.length; j += step) {
      const s = list[j]!;
      if (g(ctx, s).height > EPS) return s;
    }
    return undefined;
  };

  for (let i = list.length - 1; i >= 0; i--) {
    const child = list[i]!;
    const cg = g(ctx, child);
    const top = cg.top;
    const bottom = cg.top + cg.height;
    if (top > limit + EPS) continue;
    // A child that draws nothing is not a break site of its own: breaking after
    // it is the same as breaking before the next visible child, and letting it
    // offer a break here would strand the heading it sits behind.
    if (cg.height <= EPS) continue;

    const next = visible(i + 1, 1);
    if (bottom > limit + EPS) {
      // Child straddles the limit: try to split it. A break at the child's very
      // top is really a break *before* it and must respect keep-with-next below.
      const inner = breakInside(ctx, child, limit, minY);
      if (inner !== null && inner > minY + EPS && inner > top + EPS) return inner;
    } else {
      // Child ends above the limit: break after it (keep the gap on this page).
      const candidate = next ? Math.min(g(ctx, next).top, limit) : Math.min(bottom, limit);
      const blocked = child.flow.keepWithNext && !!next;
      if (!blocked && candidate > minY + EPS) return candidate;
      if (!blocked) return null;
    }

    // Break before this child.
    const prev = visible(i - 1, -1);
    const prevBlocks = !!prev && prev.flow.keepWithNext;
    if (!prevBlocks && top > minY + EPS) return top;
    if (!prevBlocks) return null;
    // Previous sibling insists on staying with us: keep walking back so the
    // break lands before it (or inside it if it is splittable).
  }
  return null;
}

function breakInside(ctx: BreakCtx, node: ResolvedNode, limit: number, minY: number): number | null {
  const ng = g(ctx, node);
  const fitsOnFreshPage = ng.height <= ctx.pageHeight + EPS;
  if (node.flow.keepTogether && fitsOnFreshPage) return null;

  if (node.role === "row" && node.children.length > 1) {
    const allowSplit = node.props.allowSplit === true;
    if (!allowSplit && fitsOnFreshPage) return null;
    return columnsAgree(ctx, node.children, limit, minY);
  }

  switch (node.flow.split) {
    case "none":
      return null;
    case "lines": {
      const lines = ng.lines;
      if (!lines || lines.length < 2) return null;
      const minOrphans = Math.max(1, node.flow.minOrphans);
      const minWidows = Math.max(1, node.flow.minWidows);
      for (let i = lines.length - 2; i >= 0; i--) {
        const y = lines[i]!;
        if (y > limit + EPS) continue;
        const before = i + 1;
        const after = lines.length - before;
        if (before < minOrphans) return null;
        if (after < minWidows) continue;
        return y > minY + EPS ? y : null;
      }
      return null;
    }
    case "rows": {
      const rows = ng.rows;
      if (!rows || rows.length === 0) return null;
      const body = rows.filter((r) => r.kind === "body");
      if (body.length < 2) return null;
      const minOrphans = Math.max(1, node.flow.minOrphans);
      const minWidows = Math.max(1, node.flow.minWidows);
      for (let i = body.length - 2; i >= 0; i--) {
        const r = body[i]!;
        const y = r.top + r.height;
        if (y > limit + EPS) continue;
        const before = i + 1;
        const after = body.length - before;
        if (before < minOrphans) return null;
        if (after < minWidows) continue;
        return y > minY + EPS ? y : null;
      }
      return null;
    }
    case "children":
      return bestBreak(ctx, node.children, limit, minY);
  }
}

/** Finds a y that is a valid break in every column simultaneously. */
function columnsAgree(ctx: BreakCtx, columns: ResolvedNode[], limit: number, minY: number): number | null {
  let y = limit;
  for (let iter = 0; iter < 64; iter++) {
    let yNext = y;
    for (const col of columns) {
      const kids = col.children;
      if (kids.length === 0) continue;
      const first = g(ctx, kids[0]!).top;
      const lastG = g(ctx, kids[kids.length - 1]!);
      const last = lastG.top + lastG.height;
      if (y <= first + EPS || y >= last - EPS) continue;
      const b = bestBreak(ctx, kids, y, minY);
      if (b === null) return null;
      if (b < yNext) yNext = b;
    }
    if (Math.abs(yNext - y) < EPS) return y > minY + EPS ? y : null;
    y = yNext;
  }
  return null;
}

/* ------------------------------------------------------------------ */
/* Section helpers                                                     */
/* ------------------------------------------------------------------ */

function collectTables(node: ResolvedNode, out: ResolvedNode[] = []): ResolvedNode[] {
  if (node.flow.split === "rows") out.push(node);
  for (const c of node.children) collectTables(c, out);
  return out;
}

function repeatedHeadersAt(geom: SectionGeometry, tables: ResolvedNode[], from: number): RepeatedHeader[] {
  const out: RepeatedHeader[] = [];
  for (const t of tables) {
    const tg = geom.nodes[t.key];
    if (!tg?.rows) continue;
    const headerRows = tg.rows.filter((r) => r.kind === "header");
    if (headerRows.length === 0) continue;
    const bottom = tg.top + tg.height;
    if (tg.top < from - EPS && from < bottom - EPS) {
      const height = headerRows.reduce((a, r) => a + r.height, 0);
      out.push({ tableKey: t.key, height, left: tg.left, width: tg.width });
    }
  }
  return out;
}

/**
 * Offsets inside a section at which a page break was explicitly asked for.
 *
 * A "page break" block, or any block whose flow says `breakBefore`/`breakAfter:
 * "page"`, is an instruction rather than a preference: unlike keep-together it
 * is never traded away to make content fit. Section-level breaks are handled by
 * the page loop; these are the ones nested inside a section's own content.
 */
interface ForcedBreaks {
  /** Offsets strictly inside the section's content, where it must be split. */
  inside: number[];
  /** A break before the first thing in the section: it starts a page. */
  atStart: boolean;
  /** A break after the last thing in the section: what follows starts a page. */
  atEnd: boolean;
}

function forcedBreaks(section: ResolvedNode, geom: SectionGeometry): ForcedBreaks {
  const offsets = new Set<number>();
  let contentTop = Number.POSITIVE_INFINITY;
  let contentBottom = 0;
  const visit = (n: ResolvedNode) => {
    const g = geom.nodes[n.key];
    if (g) {
      if (n.flow.breakBefore === "page") offsets.add(g.top);
      if (n.flow.breakAfter === "page") offsets.add(g.top + g.height);
    }
    n.children.forEach(visit);
  };
  for (const child of section.children) {
    const g = geom.nodes[child.key];
    if (g) {
      contentTop = Math.min(contentTop, g.top);
      contentBottom = Math.max(contentBottom, g.top + g.height);
    }
    visit(child);
  }
  if (!Number.isFinite(contentTop)) contentTop = 0;

  // A break at either edge of the content is not a split — it is a statement
  // about the section itself, the same thing `breakBefore`/`breakAfter` says.
  // Measured against the content rather than the section box, so a section's
  // own padding does not turn the break into a page of whitespace.
  return {
    inside: [...offsets].filter((y) => y > contentTop + EPS && y < contentBottom - EPS).sort((a, b) => a - b),
    atStart: [...offsets].some((y) => y <= contentTop + EPS),
    atEnd: [...offsets].some((y) => y >= contentBottom - EPS)
  };
}

/* ------------------------------------------------------------------ */
/* Pagination                                                          */
/* ------------------------------------------------------------------ */

/**
 * Pure, deterministic pagination. Given resolved sections and their measured
 * geometry, assigns clip-window fragments to pages.
 */
export function paginate(opts: PaginateOptions): PagedDocument {
  const { document, geometry, page } = opts;
  const pageHeight = page.contentHeight;
  const warnings: LayoutWarning[] = [];
  const pages: Page[] = [];

  let current: Page = { index: 0, number: 1, fragments: [], first: true };
  let y = 0;
  let pendingBreak = false;

  const newPage = () => {
    pages.push(current);
    current = { index: pages.length, number: pages.length + 1, fragments: [], first: false };
    y = 0;
    pendingBreak = false;
  };

  for (const section of document.body) {
    const geom = geometry[section.key];
    if (!geom) {
      warnings.push({ code: "forced-split", message: "Section was not measured", sectionKey: section.key, nodeId: section.nodeId });
      continue;
    }
    const H = geom.height;
    const ctx: BreakCtx = { geom, pageHeight };
    const tables = collectTables(section);
    const forced = forcedBreaks(section, geom);
    const contentBottom = section.children.reduce((m, c) => {
      const cg = geom.nodes[c.key];
      return cg ? Math.max(m, cg.top + cg.height) : m;
    }, 0);

    if ((pendingBreak || section.flow.breakBefore === "page" || forced.atStart) && y > 0) newPage();
    if (section.flow.keepTogether && H > pageHeight - y + EPS && H <= pageHeight + EPS && y > 0) newPage();

    if (H <= EPS) {
      // Empty section: still occupies nothing but keeps its order.
      continue;
    }

    let from = 0;
    let guard = 0;
    while (from < H - EPS) {
      if (++guard > 10_000) {
        warnings.push({ code: "forced-split", message: "Pagination aborted after too many fragments", sectionKey: section.key, nodeId: section.nodeId });
        break;
      }
      const avail = pageHeight - y;
      const headers = repeatedHeadersAt(geom, tables, from);
      const extra = headers.reduce((a, h) => a + h.height, 0);
      // This pass may not run past an explicit break, whatever else would fit.
      const stop = forced.inside.find((f) => f > from + EPS) ?? H;
      const lastPass = stop >= H - EPS;
      const remaining = stop - from;

      if (remaining + extra <= avail + EPS) {
        current.fragments.push({ sectionKey: section.key, from, to: stop, repeatHeaders: headers, y, height: remaining + extra, continued: from > 0, continues: !lastPass });
        y += remaining + extra;
        from = stop;
        if (lastPass) break;
        newPage();
        continue;
      }

      const limit = Math.min(from + avail - extra, stop);
      let to: number | null = null;
      if (limit > from + EPS) {
        // Section-level keep rules are handled above; break inside the section body.
        to = section.flow.split === "children" ? bestBreak(ctx, section.children, limit, from) : null;
        if (to !== null && to <= from + EPS) to = null;
        if (to !== null && to > limit + EPS) to = null;
        // Only trailing section padding remains: absorb it rather than orphaning whitespace.
        if (lastPass && to !== null && to >= contentBottom - EPS) {
          current.fragments.push({ sectionKey: section.key, from, to: H, repeatHeaders: headers, y, height: Math.min(H - from + extra, avail), continued: from > 0, continues: false });
          y += Math.min(H - from + extra, avail);
          from = H;
          break;
        }
      }

      if (to === null) {
        if (y > 0 || current.fragments.length > 0) {
          // Nothing fits in the remaining space: start a fresh page and try again.
          newPage();
          continue;
        }
        // Fresh page and still no valid break: force a raw clip so we always progress.
        if (limit <= from + EPS) {
          warnings.push({ code: "oversized-header", message: "A repeated table header is taller than the page", sectionKey: section.key, nodeId: section.nodeId, page: current.number });
          to = Math.min(H, from + Math.max(1, pageHeight));
        } else {
          to = Math.min(H, limit);
          warnings.push({ code: "forced-split", message: "Content taller than a page had to be cut at an arbitrary position", sectionKey: section.key, nodeId: section.nodeId, page: current.number });
        }
      }

      current.fragments.push({ sectionKey: section.key, from, to, repeatHeaders: headers, y, height: to - from + extra, continued: from > 0, continues: true });
      from = to;
      newPage();
    }

    if (section.flow.breakAfter === "page" || forced.atEnd) pendingBreak = true;
  }

  pages.push(current);

  // Trim a trailing empty page created by a final break/newPage.
  while (pages.length > 1 && pages[pages.length - 1]!.fragments.length === 0) pages.pop();
  // A break after the last content has nothing to push onto a new page, and a
  // report that ends on a blank page looks broken. Say so rather than doing
  // nothing silently, which reads as the break being ignored.
  if (pendingBreak) {
    warnings.push({ code: "no-op-break", message: "A page break after the last content has nothing to move to a new page", page: pages.length });
  }

  const minPages = Math.max(1, opts.minPages ?? 1);
  while (pages.length < minPages) pages.push({ index: pages.length, number: pages.length + 1, fragments: [], first: pages.length === 0 });

  if (document.body.length === 0) warnings.push({ code: "empty-document", message: "The document has no content" });

  // Region overflow warnings
  if (opts.regionGeometry) {
    const check = (sections: ResolvedNode[] | undefined, limit: number, region: string) => {
      if (!sections) return;
      const total = sections.reduce((a, s) => a + (opts.regionGeometry![s.key]?.height ?? 0), 0);
      if (total > limit + 0.5) warnings.push({ code: "region-overflow", message: `${region} content (${Math.round(total)}px) is taller than the ${region.toLowerCase()} region (${Math.round(limit)}px) and will be clipped` });
    };
    check(document.header.default, page.headerHeight, "Header");
    check(document.header.firstPage, page.headerHeight, "First-page header");
    check(document.footer.default, page.footerHeight, "Footer");
    check(document.footer.firstPage, page.footerHeight, "First-page footer");
  }

  return { pages, totalPages: pages.length, geometry: page, warnings };
}
